import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const navItems = [
  { id: 'home', label: 'Home' },
  { id: 'about', label: 'About' },
  { id: 'experience', label: 'Experience' },
  { id: 'services', label: 'Services' },
  { id: 'projects', label: 'Projects' },
  { id: 'skills', label: 'Skills' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'contact', label: 'Contact' }
];

const Navigation = ({ onContactClick }) => {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPos = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPos) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-[#0E0B1F]/80 backdrop-blur-xl shadow-lg' 
          : 'bg-transparent'
      }`}
      style={{
        borderBottom: isScrolled ? '1px solid transparent' : 'none',
        borderImage: isScrolled ? 'linear-gradient(90deg, rgba(124, 58, 237, 0.3), rgba(192, 38, 211, 0.3), rgba(255, 92, 57, 0.3)) 1' : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* MK Logo - Animated Gradient Shift with Pulsing Accent */}
        <button
          onClick={() => scrollToSection('home')}
          className="text-2xl font-bold relative group"
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          <span 
            className="bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#FF5C39] bg-clip-text text-transparent"
            style={{
              backgroundSize: '200% auto',
              animation: 'gradientShift 8s ease-in-out infinite'
            }}
          >
            MK
          </span>
          {/* Pulsing accent dot */}
          <span 
            className="absolute -top-1 -right-1 w-2 h-2 bg-[#FF5C39] rounded-full"
            style={{
              animation: 'pulse 2s ease-in-out infinite',
              boxShadow: '0 0 8px rgba(255, 92, 57, 0.6)'
            }}
          />
          <span className="absolute inset-0 opacity-0 group-hover:opacity-100 blur-xl bg-gradient-to-r from-[#7C3AED] to-[#C026D3] rounded-full transition-opacity duration-300 -z-10" />
        </button>

        {/* Desktop Navigation - Pill Container with Active State */}
        <div className="hidden lg:flex items-center px-2 py-2 bg-[#1A1430]/60 backdrop-blur-xl rounded-full border border-[#7C3AED]/20">
          {navItems.slice(1).map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`relative px-5 py-2 rounded-full text-base font-medium transition-all duration-300 ${
                activeSection === item.id
                  ? 'text-white'
                  : 'text-[#C4B5D5] hover:text-white'
              }`}
            >
              {/* Active: Solid gradient pill background */}
              {activeSection === item.id && (
                <span 
                  className="absolute inset-0 bg-gradient-to-r from-[#7C3AED] to-[#C026D3] rounded-full -z-10"
                  style={{
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                  }}
                />
              )}
              
              {/* Hover: Soft gradient-tint pill background (for inactive links) */}
              {activeSection !== item.id && (
                <span className="absolute inset-0 bg-gradient-to-r from-[#7C3AED]/0 via-[#C026D3]/20 to-[#7C3AED]/0 opacity-0 group-hover:opacity-100 rounded-full transition-opacity duration-300 -z-10" />
              )}
              
              <span className="relative z-10">{item.label}</span>
            </button>
          ))}
        </div>

        {/* CTA Button - Gradient with Arrow Icon */}
        <button
          onClick={onContactClick}
          className="hidden lg:flex items-center gap-2 relative px-6 py-2.5 rounded-full font-semibold text-sm text-white overflow-hidden group transition-all duration-300 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, #FF5C39 0%, #E54D2E 50%, #C026D3 100%)',
            boxShadow: '0 4px 15px rgba(255, 92, 57, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <span className="relative z-10">Let&rsquo;s talk</span>
          {/* Arrow icon */}
          <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
          
          {/* Animated gradient glow on hover */}
          <span 
            className="absolute inset-0 bg-gradient-to-r from-[#FF5C39] via-[#C026D3] to-[#7C3AED] opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-md"
            style={{ transform: 'scale(1.1)' }}
          />
          {/* Gradient shift on hover */}
          <span 
            className="absolute inset-0 bg-gradient-to-r from-[#7C3AED] via-[#FF5C39] to-[#C026D3] opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-full"
          />
        </button>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden p-2 text-[#EDE9FE] hover:text-[#7C3AED] transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Panel - Glassmorphism with Slide/Fade */}
          <div className="fixed top-[72px] right-0 bottom-0 w-[280px] bg-[#1A1430]/90 backdrop-blur-xl border-l border-[#7C3AED]/20 z-40 lg:hidden overflow-y-auto shadow-2xl shadow-[#7C3AED]/10 animate-slideInRight">
            <div className="flex flex-col gap-2 p-6">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-4 py-3 rounded-lg transition-all duration-300 ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-[#7C3AED]/30 to-[#C026D3]/30 text-[#EDE9FE] font-semibold border border-[#FF5C39]/30'
                      : 'text-[#9D97B5] hover:bg-[#7C3AED]/10 hover:text-[#EDE9FE]'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile CTA */}
              <button
                onClick={() => {
                  onContactClick();
                  setIsMobileMenuOpen(false);
                }}
                className="mt-4 px-6 py-3 bg-gradient-to-r from-[#FF5C39] to-[#E54D2E] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#FF5C39]/40 transition-all duration-300 text-center"
              >
                Let&rsquo;s talk
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navigation;
